import {
  faFileExport,
  faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import {
  useMemo, useState,
} from 'react';
import { useDispatch } from 'react-redux';
import {
  ButtonSize, ButtonStyle,
  IconButtonStyle,
} from '~/_shared/baseComponents/buttons';
import { LottieAnimations } from '~/_shared/baseComponents/lottieAnimation';
import { LottieAnimationTypes } from '~/_shared/baseComponents/lottieAnimation/lottieAnimation.types';
import { useTheme } from '~/_shared/themes/theme.hooks';
import { copyToClipboard } from '~/_shared/utils/clipboard/clipboard.helpers';
import type { Uuid } from '~/_shared/utils/createUuid';
import { downloadFile } from '~/_shared/utils/document/document.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import { notNullOrUndefined } from '~/_shared/utils/typeGuards';
import { processingExportRemove } from '~/store/frontendState/processing/processing.actionCreators';
import { useFileExportsSelector } from '~/store/frontendState/processing/processing.selectors';
import {
  FileExportResult, FileExportType,
} from '~/store/frontendState/processing/processing.state';
import { type TopUnderbarItem } from '../item/topUnderbarItem.types';
import {
  type TopUnderbarSavingStatusComponentProps, TopUnderbarStatusItemComponent,
} from './topUnderbarStatus.component';

const fileExportTimeRange = { fromMinutes: 2, toMinutes: 5 } as const;

const doneAnimationType = LottieAnimationTypes.CircleDownload;
const doneAnimationProps = {
  segment: LottieAnimations[doneAnimationType].segments.success,
  speed: 1,
  size: 30,
  autoplay: true,
} as const;

const exportingAnimationType = LottieAnimationTypes.CircleArrowDown;
const exportingAnimationProps = {
  segment: LottieAnimations[exportingAnimationType].segments.default,
  size: 30,
  autoplay: true,
  loop: true,
} as const;

const errorAnimationType = LottieAnimationTypes.Failed;
const errorAnimationProps = {
  segment: LottieAnimations.Failed.segments.default,
  size: 30,
  autoplay: true,
} as const;

const sampleFileExportData = [{
  id: 'sample-file-export-id-1' as Uuid,
  name: 'Export Contained Boundaries',
  type: FileExportType.BoundaryAreaQuery,
  request: {
    as_text: false,
    map_id: 1,
    boundary_group_id: 1,
    format: 'csv' as const,
    overlap_type: 'contains' as const,
  },
  resultPages: [{
    type: FileExportResult.Pending,
    fileUrl: undefined,
    data: undefined,
    error: null,
  }],
}, {
  id: 'sample-file-export-id-2' as Uuid,
  name: 'Export Some File',
  type: FileExportType.BoundaryAreaQuery,
  request: {
    as_text: false,
    map_id: 1,
    boundary_group_id: 1,
    format: 'xlsx' as const,
    overlap_type: 'partial' as const,
  },
  resultPages: [{
    type: FileExportResult.Success,
    fileUrl: 'https://example.com/export/file.xlsx',
    data: undefined,
    error: null,
  }],
}, {
  id: 'sample-file-export-id-3' as Uuid,
  type: FileExportType.BoundaryAreaQuery,
  name: 'Export File as CSV',
  request: {
    as_text: true,
    map_id: 1,
    boundary_group_id: 1,
    format: 'csv' as const,
    overlap_type: 'contains' as const,
  },
  resultPages: [{
    type: FileExportResult.Error,
    fileUrl: undefined,
    data: undefined,
    error: 'An error occurred during export.',
  }],
}];

export const useFileExportStatusItems = ({
  generateSamples,
}: {
  generateSamples?: boolean;
} = {}): ReadonlyArray<TopUnderbarItem> => {
  const [t] = useTranslation();
  const theme = useTheme();
  const dispatch = useDispatch();
  const fileExports = useFileExportsSelector();
  const [copiedItemIds, setCopiedItemIds] = useState<Set<Uuid>>(new Set());

  return useMemo(() => {
    return (generateSamples ? sampleFileExportData : fileExports).map((item) => {
      const exportId = item.id;
      const isDone = item.resultPages.every((page) => page.type === FileExportResult.Success);
      const incompletePageIndex = item.resultPages.findIndex((page) => page.type === FileExportResult.Pending);
      const error = item.resultPages.find((page) => page.type === FileExportResult.Error)?.error;
      const exportAsText = item.request.as_text ?? false;

      let animation: TopUnderbarSavingStatusComponentProps['animation'] = {
        type: exportingAnimationType,
        ...exportingAnimationProps,
      };
      if (isDone) {
        animation = {
          type: doneAnimationType,
          ...doneAnimationProps,
        };
      }
      else if (error) {
        animation = {
          type: errorAnimationType,
          ...errorAnimationProps,
        };
      }

      const isCopied = copiedItemIds.has(exportId);

      const copyToClipboardButtonProps = exportAsText && isDone && item.resultPages[0]?.data
        ? {
          buttonStyle: ButtonStyle.Primary,
          onClick: () => {
            copyToClipboard(item.resultPages[0]?.data ?? '');
            setCopiedItemIds(prev => new Set(prev).add(exportId));
            setTimeout(() => {
              setCopiedItemIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(exportId);
                return newSet;
              });
            }, 3000);
          },
          text: t('Copy to Clipboard'),
          tooltip: isCopied ? t('Copied to Clipboard!') : undefined,
        }
        : undefined;

      let button = undefined;
      if (!isDone && !error) {
        button = {
          buttonStyle: ButtonStyle.Danger,
          size: ButtonSize.Small,
          text: t('Cancel'),
          onClick: () => {
            dispatch(processingExportRemove(exportId));
          },
        };
      }
      else if (isDone && !error && !exportAsText) {
        button = {
          buttonStyle: ButtonStyle.Primary,
          size: ButtonSize.Small,
          text: t('Download again'),
          onClick: () => {
            const downloadLinks = item.resultPages.map((page) => {
              if (page.type === FileExportResult.Success && page.fileUrl) {
                return page.fileUrl;
              }
              return null;
            }).filter(notNullOrUndefined);
            downloadLinks.forEach((link, index) => {
              setTimeout(() => {
                downloadFile(link);
              }, index * 2000);
            });
          },
        };
      }
      else if (copyToClipboardButtonProps) {
        button = copyToClipboardButtonProps;
      }

      let suffixText = undefined;
      if (error) {
        suffixText = {
          value: error,
          color: theme.textColors.danger,
        };
      }
      else if (!exportAsText && incompletePageIndex > 0 && !isDone) {
        suffixText = {
          value: `${t('Exporting Page {n}', { n: incompletePageIndex + 1 })}. ${t('The download should start automatically.')}`,
          color: theme.textColors.warning,
        };
      }
      else if (!isDone) {
        suffixText = {
          value: t('This process can take up to {{fromMinutes}}-{{toMinutes}} minutes.', fileExportTimeRange),
          color: theme.textColors.warning,
        };
      }
      else if (!exportAsText && isDone) {
        suffixText = {
          value: `${t('Export Complete')}. ${t('The download should start automatically.')}`,
          color: theme.textColors.warning,
        };
      }

      return {
        key: `file-export-${item.id}`,
        sticky: true,
        icon: faFileExport,
        colors: {
          color: theme.contrasts.selectedAccent2,
          backgroundColor: theme.contrasts.selectedAccent2Background,
        },
        children: (
          <TopUnderbarStatusItemComponent
            animation={animation}
            text={{
              value: t('Exporting'),
              color: theme.textColors.light,
            }}
            subtexts={[
              {
                value: `• ${item.name}`,
              },
            ]}
            suffixText={suffixText}
            button={button}
            iconButton={(isDone || error)
              ? {
                icon: faXmark,
                onClick: () => {
                  dispatch(processingExportRemove(exportId));
                },
                buttonStyle: IconButtonStyle.Secondary,
                size: 's',
              } : undefined
            }
          />
        ),
      };
    });
  }, [copiedItemIds, dispatch, fileExports, generateSamples, t, theme.contrasts.selectedAccent2, theme.contrasts.selectedAccent2Background, theme.textColors.danger, theme.textColors.light, theme.textColors.warning]);
};
